import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientsAPI } from '../api';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';

const StatusBadge = ({ status }) => {
    const map = {
        Accepted: 'badge-success',
        Completed: 'badge-success',
        Pending: 'badge-warning',
        Cancelled: 'badge-danger',
    };
    return (
        <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>
    );
};

export default function PatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        patientsAPI
            .getById(id)
            .then((res) => setPatient(res.data.data))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading)
        return (
            <div className="loading-container">
                <div className="spinner" />
            </div>
        );
    if (!patient) return <div>Patient not found</div>;

    return (
        <div className="fade-in">
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 28,
                }}
            >
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/patients')}
                >
                    <ArrowLeft size={14} /> Back
                </button>
                <h1 className="page-title" style={{ fontSize: 22 }}>
                    Patient Profile
                </h1>
            </div>

            <div className="grid-2">
                <div
                    className="card"
                    style={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 24,
                    }}
                >
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 18,
                            background:
                                'linear-gradient(135deg, #6366f1, #a78bfa)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 26,
                            fontWeight: 700,
                            color: 'white',
                        }}
                    >
                        {patient.full_name?.[0]}
                    </div>
                    <div>
                        <h2
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 22,
                                fontWeight: 700,
                                marginBottom: 4,
                            }}
                        >
                            {patient.full_name}
                        </h2>
                        <p style={{ color: 'var(--text3)', fontSize: 13 }}>
                            {patient.profile_id}
                        </p>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                            <span
                                className={`badge ${patient.status === 'Active' ? 'badge-success' : 'badge-warning'}`}
                            >
                                {patient.status}
                            </span>
                            <span
                                className={`badge ${patient.is_verified === 'Yes' ? 'badge-success' : 'badge-warning'}`}
                            >
                                {patient.is_verified === 'Yes'
                                    ? 'Verified'
                                    : 'Unverified'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        Personal Info
                    </h3>
                    {[
                        ['Email', patient.email],
                        [
                            'Mobile',
                            `${patient.country_code} ${patient.mobile_number}`,
                        ],
                        ['Age', patient.age],
                        ['Gender', patient.gender],
                        ['Country', patient.country_name],
                        [
                            'Joined',
                            new Date(patient.created_at).toLocaleDateString(),
                        ],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                </div>

                {patient.addresses?.length > 0 && (
                    <div className="card">
                        <h3
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            Saved Addresses
                        </h3>
                        {patient.addresses.map((a) => (
                            <div
                                key={a.address_id}
                                style={{
                                    padding: '10px 0',
                                    borderBottom: '1px solid var(--border)',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        marginBottom: 4,
                                    }}
                                >
                                    <span className="tag">
                                        {a.address_type}
                                    </span>
                                    {a.is_default === 'Yes' && (
                                        <span
                                            className="badge badge-info"
                                            style={{ fontSize: 11 }}
                                        >
                                            Default
                                        </span>
                                    )}
                                </div>
                                <p
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text2)',
                                    }}
                                >
                                    {a.full_address}
                                </p>
                                {a.landmark && (
                                    <p
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--text3)',
                                        }}
                                    >
                                        Near {a.landmark}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Appointments */}
                <div className="card p-0" style={{ gridColumn: '1 / -1', padding: 0 }}>
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 24,
                            marginInlineStart: 24,
                            marginTop: 24,
                        }}
                    >
                        Appointment History
                    </h3>
                    {patient.appointments?.length === 0 ? (
                        <div className="empty-state">
                            <p>No appointments yet</p>
                        </div>
                    ) : (
                        <div
                            className="table-wrapper"
                            style={{ border: 'none', borderRadius: 0 }}
                        >
                            <table>
                                <thead>
                                    <tr>
                                        <th>Doctor</th>
                                        <th>Specialization</th>
                                        <th>Type</th>
                                        <th>Date & Time</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {patient.appointments.map((a) => (
                                        <tr key={a.appointment_id}>
                                            <td
                                                style={{ color: 'var(--text)' }}
                                            >
                                                {a.doctor_name || '—'}
                                            </td>
                                            <td>{a.specialization}</td>
                                            <td>
                                                <span className="tag">
                                                    {a.appointment_type}
                                                </span>
                                            </td>
                                            <td>
                                                {a.appointment_date}{' '}
                                                {a.appointment_time}
                                            </td>
                                            <td>
                                                <StatusBadge
                                                    status={a.status}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
